/**
 * Client for the compliance archive of decommissioned assets (#780) —
 * GET /customers/:customer/assets/archived on tri-server.
 *
 * A device decommissioned in the app is detached from its floor plan server-side, so it
 * disappears from every floor-scoped view. Nothing about it is deleted; this endpoint is the
 * read path that keeps it — and the audit trail of the transition — accessible.
 */

import { configuration } from "./Configuration";
import { ApplicationError, UnauthorizedError } from "./general/Errors";
import { authenticatedFetch } from "./JWTAuthService";

/** Where the device used to hang. Ids, not names — the panel resolves them. */
export interface ArchivedLocation {
  floor?: string;
  space?: string;
  x?: number;
  y?: number;
}

/** One row of the AssetStateAudit trail. */
export interface AssetStateChange {
  from?: string;
  to?: string;
  changedBy?: string;
  changedAt?: string;
  reason?: string | null;
  archivedLocation?: ArchivedLocation;
}

export interface ArchivedAsset {
  _id: string;
  /** Business id (the number on the device), as opposed to the Mongo `_id`. */
  id?: string;
  name?: string;
  schemaCategory?: string;
  product?: string;
  decommissionedAt?: string;
  archivedLocation?: ArchivedLocation;
  /** Newest first, as returned by the server. */
  _stateHistory?: AssetStateChange[];
}

const processResponse = async (response: Response) => {
  if (response.ok) {
    return response.json();
  }

  if (response.status === 401) {
    throw new UnauthorizedError();
  }

  let errorMessage = `${response.status} ${response.statusText}`;

  try {
    const errorData = await response.json();
    errorMessage = errorData.message || errorData.error || errorMessage;
  } catch {
    // response body not JSON — keep default message
  }

  throw new ApplicationError(errorMessage);
};

export const useAssetArchiveAPI = (customer: string) => {
  const baseUrl = `${configuration.serverUrl}/customers/${customer}/assets`;

  return {
    getArchivedAssets: async (limit?: number): Promise<ArchivedAsset[]> => {
      const query = limit ? `?limit=${limit}` : "";
      const response = await authenticatedFetch(`${baseUrl}/archived${query}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      const result = await processResponse(response);

      return Array.isArray(result) ? result : [];
    },
  };
};
